import React, { useEffect, useRef, useState } from 'react';
import { GetStarted } from '../get-started';

const steps = [
  {
    title: 'EPC Rating',
    description: `Provide us with your postcode to learn more about your property's energy performance. Add a few extra details to find out if the home is eligible for free improvements.`,
    image: '/images/steps/step-1.png',
  },
  {
    title: 'Energy Assessment',
    description: `One of our retrofit assessors will visit the property and conduct an in-depth survey of its current and potential energy efficiency.
Available for £195 (inc VAT) which will be refunded if you choose to proceed with us for an installation.`,
    image: '/images/steps/step-2.png',
  },
  {
    title: 'Improvement Plan',
    description: `You'll receive a detailed plan including home improvement recommendations a breakdown of costs, along with any grants the property is eligible for.`,
    image: '/images/steps/step-3.png',
  },
  {
    title: 'Install',
    description: `You can then choose to use our skilled in-house team to install the recommended improvements or choose your own installers.`,
    image: '/images/steps/step-4.png',
  },
  {
    title: 'Job Complete',
    description: `After installation, you will soon notice the positive impacts on your property and your bills! We will also lodge your new and improved EPC rating.`,
    image: '/images/steps/step-5.png',
  },
];

const HowItWorks = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const { top, height, bottom } = container.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const maxScrollable = height - windowHeight;

      let scrollProgress = 0;

      if (top >= 0) {
        scrollProgress = 0;
      } else if (bottom <= windowHeight) {
        scrollProgress = 100;
      } else {
        const scrolled = Math.min(-top, maxScrollable);
        scrollProgress = (scrolled / maxScrollable) * 100;
      }

      setProgress(scrollProgress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="mx-auto max-w-7xl">
      <h1 className="pb-6 pl-6 pt-12 text-2xl lg:text-5xl">How it all works</h1>
      <div className="flex flex-col items-center px-4 lg:px-52" ref={containerRef}>
        <div className="hidden md:block">
          <div className="relative w-full max-w-6xl px-4">
            <div className="absolute inset-y-0 left-1/2 z-0 w-[6px] -translate-x-1/2 rounded-full bg-[#CBD3D6]" />
            <div
              className="absolute left-1/2 z-10 w-[6px] -translate-x-1/2 bg-[#2A9C8E]"
              style={{ height: `${progress}%`, top: 0 }}
            />
            <div className="relative z-20 flex flex-col gap-20 py-4">
              {steps.map((step, index) => {
                const isReversed = index === 1 || index === 3;

                return (
                  <div
                    key={step.title}
                    className={`grid grid-cols-1 items-center gap-6 md:grid-cols-[1fr_auto_1fr] ${
                      isReversed ? 'md:grid-cols-[1fr_auto_1fr]' : ''
                    }`}
                  >
                    {isReversed ? (
                      <>
                        <div className="relative flex flex-col gap-4 text-right">
                          <div className="flex flex-col items-end gap-4">
                            <div className="relative flex size-12 items-center justify-center rounded-full">
                              <div
                                className="absolute inset-0 rounded-full p-[2px]"
                                style={{
                                  background:
                                    'conic-gradient(#0D692C, #2B9148, #80C066, #E9E669, #E9C469, #F4A261, #E76F51, #0D692C)',
                                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                  WebkitMaskComposite: 'xor',
                                  maskComposite: 'exclude',
                                  borderRadius: '9999px',
                                }}
                              />
                              <span className="font-jost relative z-10 text-base font-medium leading-[100%] md:text-2xl">
                                {index + 1}
                              </span>
                            </div>

                            <h2 className="text-xl font-medium md:text-2xl">{step.title}</h2>
                          </div>
                          <p className="text-base leading-[28px] text-[#516B75] md:leading-[30px]">
                            {step.description}
                          </p>
                        </div>
                        <div className="hidden w-[6px] md:block" />
                        <div className="flex items-center justify-center md:justify-start">
                          <img src={step.image} alt={step.title} className="h-auto w-[200px] object-contain" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-center md:justify-end">
                          <img src={step.image} alt={step.title} className="h-auto w-[200px] object-contain" />
                        </div>
                        <div className="hidden w-[6px] md:block" />
                        <div className="relative flex flex-col gap-4 text-left">
                          <div className="flex flex-col items-start gap-4">
                            <div className="relative flex size-12 items-center justify-center rounded-full">
                              <div
                                className="absolute inset-0 rounded-full p-[2px]"
                                style={{
                                  background:
                                    'conic-gradient(#0D692C, #2B9148, #80C066, #E9E669, #E9C469, #F4A261, #E76F51, #0D692C)',
                                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                  WebkitMaskComposite: 'xor',
                                  maskComposite: 'exclude',
                                  borderRadius: '9999px',
                                }}
                              />
                              <span className="font-jost relative z-10 text-base font-medium leading-[100%] md:text-2xl">
                                {index + 1}
                              </span>
                            </div>
                            <h2 className="text-xl font-medium md:text-2xl">{step.title}</h2>
                          </div>
                          <p className="text-base leading-[28px] text-[#516B75] md:leading-[30px]">
                            {step.description}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="relative block w-full max-w-xl pl-2 md:hidden">
          <div className="absolute inset-y-0 z-0 w-[4px] -translate-x-1/2 bg-[#CBD3D6]" />
          <div
            className="absolute z-10 w-[4px] -translate-x-1/2 bg-[#2A9C8E]"
            style={{ height: `${progress}%`, top: 0 }}
          />
          {steps.map((step, index) => (
            <div key={step.title} className="relative mb-10 ml-4">
              <div className="absolute top-0 flex size-10 items-center justify-center">
                <div
                  className="absolute inset-0 p-[2px]"
                  style={{
                    background:
                      'conic-gradient(#0D692C, #2B9148, #80C066, #E9E669, #E9C469, #F4A261, #E76F51, #0D692C)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    borderRadius: '9999px',
                  }}
                />
                <span className="relative z-10 font-bold">{index + 1}</span>
              </div>
              <div className="ml-2 flex flex-col gap-3 pt-2">
                <h2 className="ml-11 text-lg font-semibold">{step.title}</h2>
                <p className="text-[#516B75]">{step.description}</p>
                <img src={step.image} alt={step.title} className="mt-2 w-[150px] max-w-xs object-contain" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex w-full flex-col items-center justify-start gap-2 px-4 pb-6 text-left md:mt-6 md:gap-6 md:text-center">
          <p className="font-jost text-xl font-medium md:w-[80%] lg:w-full lg:text-2xl">
            Ready to improve your home’s energy efficiency?
          </p>
          <div className="w-full text-left md:w-[521px]">
            <GetStarted />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
